"use client"

import styled from "@emotion/styled"
import { useContext, useState, useEffect } from "react"
import Drawer from "@mui/material/Drawer"
import CloseIcon from "@mui/icons-material/Close"
import Rating from "@mui/material/Rating"
import TickIcon from "../../Common/TickIcon"
import CrossIcon from "../../Common/CrossIcon"
import Divider from "@mui/material/Divider"
import CircularProgress from "@mui/material/CircularProgress"
import mainContext from "../../../contexts/mainContext"
import { formatWithTZ } from "../../../utils"

const anchor = "right"

const BaseDetailsDrawer = ({ canvasState, setCanvasState, config }) => {
  const [openInvoice, setOpenInvoice] = useState({ open: false })
  const { setSnackBar } = useContext(mainContext)
  const [data, setData] = useState({})
  const [availableDays, setAvailableDays] = useState([])

  const callSnackBar = (msg, type) => {
    setSnackBar({
      status: true,
      text: msg,
      severity: config.snackBarSeverity[type],
    })
  }

  const getDetail = async () => {
    const res = await config.fetchDetails(config.getId(canvasState))
    if (res && res.status === 500) return callSnackBar(config.snackBarText.internalServerError, "error")
    if (res.data.status !== 200) return callSnackBar(res.data.errors[0].message, "error")

    setData(res.data.data)
    const availability = config.getAvailability(res.data.data) || []

    // Use startUTCTime from the first slot in availabilityTime, since there is one entry per day
    const days = availability
      .map((e) => {
        const firstSlot = e.availabilityTime?.[0]
        if (!firstSlot) return null
        return formatWithTZ(firstSlot.startUTCTime, "ddd")
      })
      .filter(Boolean)

    setAvailableDays(days)
  }

  const handleInvoice = () => {
    setOpenInvoice({ open: true })
  }

  useEffect(() => {
    if (canvasState.open) getDetail()
    // eslint-disable-next-line
  }, [canvasState])

  const profileData = config.getProfileData(data)
  const role = config.getRole(data)
  const themeClass = role === "HouseKeeper" ? "hk-theme-color" : "bb-theme-color"

  return (
    <>
      {openInvoice.open && config.InvoiceComponent && (
        <config.InvoiceComponent
          open={openInvoice}
          setOpen={setOpenInvoice}
          applicationId={config.getInvoiceId(data)}
        />
      )}
      <Drawer anchor={anchor} open={canvasState.open} onClose={() => setCanvasState({ open: false })}>
        <Container>
          {Object.keys(data).length > 0 ? (
            <>
              <Header>
                <h4>{config.drawerTitle}</h4>
                <CloseIcon className="pointer" onClick={() => setCanvasState({ open: false })} />
              </Header>
              <Body className="scroll-bar-hidden">
                <ProfileSection>
                  <div className="profile_pic">
                    <img src={profileData.profilePicture || "/placeholder.svg"} alt="" />
                  </div>
                  <div className="details">
                    <div className="info">
                      <strong>{profileData.name}</strong>
                      <div className="reviews">
                        <Rating
                          name="read-only"
                          value={profileData.reviews.average}
                          readOnly
                          sx={{ fontSize: "95%" }}
                        />
                        <br />
                        {profileData.reviews.count} reviews
                      </div>
                    </div>
                    <div className="about">{profileData.about}</div>
                    {profileData.children && <div className="childrens">{profileData.children}</div>}
                  </div>
                </ProfileSection>

                {config.getTagsSection && config.getTagsSection(data, themeClass)}

                <WeekendsDetails>
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
                    <div className={availableDays.includes(day) ? "active" : ""} key={day}>
                      <strong>{day.toUpperCase()}</strong>
                      {availableDays.includes(day) ? <TickIcon /> : <CrossIcon />}
                    </div>
                  ))}
                </WeekendsDetails>

                <TimeDetails>
                  {config.getAvailability(data).map((e) => {
                    const firstSlot = e.availabilityTime?.[0] // first index
                    return (
                      <div key={e._id}>
                        {/* Dates from first slot UTC times */}
                        <div className={themeClass}>
                          {firstSlot
                            ? `${formatWithTZ(firstSlot.startUTCTime, "ddd, Do MMM, YYYY")} to ${formatWithTZ(
                                firstSlot.endUTCTime,
                                "ddd, Do MMM, YYYY"
                              )}`
                            : "-"}
                        </div>

                        {/* Time slots */}
                        {e.availabilityTime &&
                          e.availabilityTime.map((e2) => (
                            <div key={e2._id}>
                              From {formatWithTZ(e2.startUTCTime)} to {formatWithTZ(e2.endUTCTime)}h
                            </div>
                          ))}
                      </div>
                    )
                  })}
                </TimeDetails>

                <Divider className="divider-gap" />

                <Details>{config.getDetailsSection(data, themeClass)}</Details>

                {config.getActionButtons && config.getActionButtons(data, canvasState, handleInvoice)}
              </Body>
            </>
          ) : (
            <div style={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress />
            </div>
          )}
        </Container>
      </Drawer>
    </>
  )
}

// Styled components
const Container = styled("div")`
    height: 100vh;
    width: 30vw;
    display: flex;
    flex-direction: column;
    padding: 1rem;
    overflow: hidden;
    min-width:300px;
    .active{
        color: var(--nona-theme-color);
    }
    .divider-gap{
        margin: 1rem 0;
    }
`

const Header = styled("div")`
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    h4{
        font-weight: 900;
        font-size: 120%;
    }
    padding-bottom: 0.7rem;
    border-bottom: 1px solid var(--card-border);
`

const Body = styled("div")`
    margin-top: 1rem;
    height: 100%;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
`

const ProfileSection = styled("div")`
    display: grid;
    grid-template-columns: 1fr 2fr;
    .profile_pic{
        height: 80px;
        width: 80px;
        border-radius: 10px;
        border: 2px solid var(--theme-color);
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        img{
            width: 150%;
            clip-path: circle();
            object-fit: contain;
        }
    } 
    .details{
        font-size: 80%;
        .info{
            display: flex;
            justify-content: space-between;
            column-gap: 1rem;
            font-size: 90%;
            strong{
                font-size: 130%;
            }
        }
        .about{
            margin-top: 1rem;
        }
        .childrens{
            margin-top: 0.5rem;
        }
    }
`

const WeekendsDetails = styled("div")`
    margin-top: 1rem;
    font-size: 80%;
    display: flex;
    justify-content: space-around;
    column-gap: 0.5rem;
    div{
        display: grid;
        row-gap: 0.5rem;
    }
`

const TimeDetails = styled("div")`
    margin-top: 1rem;
    font-size: 80%;
    font-family: "semi-bold";
   div{
       margin-bottom: 0.2rem;
       display: flex;
       justify-content: space-between;
   } 
`

const Details = styled("div")`
    font-size: 80%;
    font-family: "regular";
   div{
       justify-content: space-between;
   } 
`

export default BaseDetailsDrawer
